import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
/**
 * Waits for loaders to settle (including auth) and then scrolls to the `#hash` part of the URL.
 * (ie: user navigates to: example.com/somepage#hash)
 *
 * Checks whether:
 * - sign-in is in progress
 * - data is loading
 * - hash exists
 * - container with id matching hash exists
 *
 * @example Connection with `react-query`
 * ```tsx
 * // React query or graphql API data hook
 * const { data, isLoading } = useGetSomeDataQuery();
 * // Pass to this hook to support automatic scrolling to the hash in the URL
 * useScrollToHashAfterLoad(isLoading, data);
 * ```
 * Page will now scroll to URL hashes when `useGetSomeDataQuery` is done loading.
 */
export const useScrollToHashAfterLoad = (isLoading, data) => {
    const { inProgress } = useMsal();
    const isLoadingAuth = inProgress !== InteractionStatus.None;
    const { hash } = useLocation();
    const el = !isLoadingAuth &&
        !isLoading &&
        data &&
        (hash === null || hash === void 0 ? void 0 : hash.length) > 1 &&
        document.querySelector(hash);
    useEffect(() => {
        if (el) {
            el.scrollIntoView();
        }
    }, [hash, data, el]);
};
